import { useMutation, useQueryClient } from '@tanstack/react-query';

import { PairFloatFloat } from '@/api/data-contracts';
import { useMediaPlansService } from '@/features/media-plans';
import { useOrdersService } from '@/features/orders';
import { useTasks } from '@/features/service-tasks';
import { queryKeys } from '@/react-query/constants';
import mixpanelEvents from '@/shared/lib/analytics/mixpanel-events';
import useMixpanel from '@/shared/lib/analytics/mixpanel.service';
import {
  getWatermarkKey,
  setStoredChannelWatermark,
  setStoredWatermark,
} from '@/shared/lib/commentWatermarkUtils';
import { useSessionStableWatermarks } from '@/shared/lib/hooks/useSessionStableWatermarks';

export interface UseCommentMutationsParams {
  mediaPlanId?: string;
  orderId?: string;
  locationId?: string;
  taskId?: string;
  bookingId?: string;
  attachmentId?: string;
  channel?: string;
  userType?: 'location' | 'advertiser' | 'service' | 'backoffice' | undefined;
  useServicesEndpoints?: boolean;
}

/**
 * The comment create/edit/delete mutations for useComments, lifted into the api layer.
 * Re-acquires the per-domain services and effect hooks itself (all idempotent) and takes
 * only the scope ids/flags, so the mutation bodies stay identical to their in-hook form.
 */
export const useCommentMutations = ({
  mediaPlanId,
  orderId,
  locationId,
  taskId,
  bookingId,
  attachmentId,
  channel,
  userType,
  useServicesEndpoints,
}: UseCommentMutationsParams) => {
  const queryClient = useQueryClient();
  const {
    addComment: addMediaPlanComment,
    editComment: editMediaPlanComment,
    deleteComment: deleteMediaPlanComment,
  } = useMediaPlansService();
  const {
    addComment: addOrderComment,
    addServicesOrderComment,
    updateComment: updateOrderComment,
    deleteComment: deleteOrderComment,
  } = useOrdersService();
  const {
    addServiceTaskComment: addTaskComment,
    addServiceTaskAttachmentComment: addAttachmentComment,
    editServiceTaskComment: editTaskComment,
    deleteServiceTaskComment: deleteTaskComment,
  } = useTasks();
  const { trackEvent } = useMixpanel();
  const { clearSnapshot } = useSessionStableWatermarks();

  const { mutate: saveMediaPlanComment } = useMutation(
    ({
      message,
      parentId,
      files,
      coordinates,
    }: {
      message: string;
      files?: File[];
      parentId?: string;
      coordinates?: PairFloatFloat;
    }) =>
      addMediaPlanComment(mediaPlanId!, {
        request: { message, parentId, coordinates },
        files: files ?? [],
      }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.addComment, {
          'Plan Id': mediaPlanId,
        });

        const now = new Date().toISOString();
        setStoredWatermark(getWatermarkKey('MEDIA_PLAN', mediaPlanId!), now);
        clearSnapshot('MEDIA_PLAN', mediaPlanId!);
        await queryClient.invalidateQueries([queryKeys.comments, 'mediaPlan', mediaPlanId]);
      },
    },
  );

  const { mutate: saveOrderComment } = useMutation(
    ({ message, parentId, files }: { message: string; files?: File[]; parentId?: string }) =>
      addOrderComment(orderId!, {
        request: {
          message,
          parentId,
          channel,
          locationId:
            channel === 'services_advertiser' || (channel === 'team' && userType === 'advertiser')
              ? undefined
              : (locationId ?? undefined),
        },
        files: files ?? [],
      }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.addComment, {
          'Order Id': orderId,
          'Location Id': locationId,
        });

        const now = new Date().toISOString();
        if (channel) {
          // Channel-scoped stamp: posting marks this channel read without masking
          // unread state in the location's other channels (shared legacy key).
          setStoredChannelWatermark('ORDER', orderId!, locationId, channel, now);
        } else {
          setStoredWatermark(getWatermarkKey('ORDER', orderId!, locationId!), now);
        }
        clearSnapshot('ORDER', orderId!, locationId!);
        await queryClient.invalidateQueries([queryKeys.comments, 'order', orderId]);
      },
    },
  );

  const { mutate: saveServicesOrderComment } = useMutation(
    ({ message, parentId, files }: { message: string; files?: File[]; parentId?: string }) =>
      addServicesOrderComment(orderId!, locationId!, {
        request: { message, parentId, channel },
        files: files ?? [],
      }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.addComment, {
          'Order Id': orderId,
        });

        await queryClient.invalidateQueries([queryKeys.comments, 'services-order', orderId]);
      },
    },
  );

  const { mutate: editMediaPlanCommentMutation } = useMutation(
    ({
      commentId,
      message,
      coordinates,
    }: {
      commentId: string;
      message: string;
      coordinates?: PairFloatFloat;
    }) => editMediaPlanComment(commentId, { message, coordinates }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.editComment, {
          'Plan Id': mediaPlanId,
        });

        await queryClient.invalidateQueries([queryKeys.comments, 'mediaPlan', mediaPlanId]);
      },
    },
  );

  const { mutate: editOrderComment } = useMutation(
    ({ commentId, message }: { commentId: string; message: string }) =>
      updateOrderComment(commentId, { message }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.editComment, {
          'Order Id': orderId,
          'Location Id': locationId,
        });

        if (useServicesEndpoints) {
          await queryClient.invalidateQueries([queryKeys.comments, 'services-order', orderId]);
        } else {
          await queryClient.invalidateQueries([queryKeys.comments, 'order', orderId]);
        }
      },
    },
  );

  const { mutate: editTaskCommentMutation } = useMutation(
    ({ commentId, message }: { commentId: string; message: string }) =>
      editTaskComment(taskId!, commentId, { message }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.editComment, {
          'Task Id': taskId,
        });

        await queryClient.invalidateQueries({ queryKey: [queryKeys.comments, 'task', taskId] });
      },
    },
  );

  const { mutate: saveTaskComment } = useMutation(
    ({
      message,
      parentId,
      files,
      coordinates,
    }: {
      message: string;
      files?: File[];
      parentId?: string;
      coordinates?: PairFloatFloat;
    }) =>
      addTaskComment(taskId!, { request: { message, parentId, coordinates }, files: files ?? [] }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.addComment, {
          'Task Id': taskId,
        });

        const now = new Date().toISOString();
        setStoredWatermark(getWatermarkKey('TASK', bookingId ?? taskId!), now);
        clearSnapshot('TASK', bookingId ?? taskId!);
        await queryClient.invalidateQueries({ queryKey: [queryKeys.comments, 'task', taskId] });
        if (bookingId) {
          queryClient.invalidateQueries({
            queryKey: [queryKeys.comments, 'task-unread', bookingId],
          });
        }
      },
    },
  );

  const { mutate: saveAttachmentComment } = useMutation(
    ({
      message,
      parentId,
      files,
      coordinates,
    }: {
      message: string;
      files?: File[];
      parentId?: string;
      coordinates?: PairFloatFloat;
    }) =>
      addAttachmentComment(taskId!, attachmentId!, {
        request: { message, parentId, coordinates },
        files: files ?? [],
      }),
    {
      onSuccess: async () => {
        trackEvent(mixpanelEvents.addComment, {
          'Attachment Id': attachmentId,
        });

        const now = new Date().toISOString();
        setStoredWatermark(getWatermarkKey('ATTACHMENT', taskId!, attachmentId!), now);
        clearSnapshot('ATTACHMENT', taskId!, attachmentId!);
        await queryClient.invalidateQueries([
          queryKeys.comments,
          'attachments',
          taskId,
          attachmentId,
        ]);
      },
    },
  );

  const { mutate: deleteComment } = useMutation(
    (commentId: string) => {
      if (taskId) {
        return deleteTaskComment(taskId, commentId);
      } else if (mediaPlanId) {
        return deleteMediaPlanComment(commentId);
      } else {
        return deleteOrderComment(commentId);
      }
    },
    {
      onSuccess: async () => {
        const invalidations: Promise<void>[] = [];

        if (mediaPlanId) {
          invalidations.push(
            queryClient.invalidateQueries([queryKeys.comments, 'mediaPlan', mediaPlanId]),
          );
        }

        if (taskId) {
          invalidations.push(
            queryClient.invalidateQueries([queryKeys.comments, 'task', taskId]),
            queryClient.invalidateQueries([
              queryKeys.comments,
              'attachments',
              taskId,
              attachmentId,
            ]),
          );
        }

        if (orderId) {
          if (useServicesEndpoints) {
            invalidations.push(
              queryClient.invalidateQueries([queryKeys.comments, 'services-order', orderId]),
            );
          } else {
            invalidations.push(
              queryClient.invalidateQueries([queryKeys.comments, 'order', orderId]),
            );
          }
        }

        await Promise.all(invalidations);
      },
    },
  );

  return {
    saveMediaPlanComment,
    saveOrderComment,
    saveServicesOrderComment,
    saveTaskComment,
    saveAttachmentComment,
    editMediaPlanCommentMutation,
    editOrderComment,
    editTaskCommentMutation,
    deleteComment,
  };
};

export default useCommentMutations;
